// Admin → Task reminders settings panel (Phase 3). Admin-only — the whole Admin page is gated by
// RequireRole('admin') (App.tsx) and the server enforces requireRole('admin') on GET/PATCH
// /api/office-tasks/settings, so this panel is just a thin form over those endpoints. All business
// logic stays server-side; client-side validation mirrors the server clamps (values ≥ 0).

import { useEffect, useState, type CSSProperties } from 'react';
import { C, F, card, Btn, ErrorText } from './adminUi';
import * as api from '../../lib/tasks';

const inputStyle: CSSProperties = { fontFamily: F, fontSize: 13, padding: '6px 8px', border: `1px solid ${C.borderMed}`, borderRadius: 6, background: '#fff', color: C.text, width: 110 };
const labelStyle: CSSProperties = { fontWeight: 600, fontSize: 13, color: C.text, display: 'block', marginBottom: 2 };
const hintStyle: CSSProperties = { fontSize: 12, color: C.textDim, marginBottom: 8 };

// Coerce a number input to a non-negative integer (mirrors the server clamp; out-of-range → 0).
const nonNeg = (v: string): number => { const n = Math.floor(Number(v)); return Number.isFinite(n) && n > 0 ? n : 0; };

export default function TasksSettingsManager({ setToast }: { setToast: (s: string | null) => void }) {
  const [s, setS] = useState<api.ReminderSettings | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => { api.getSettings().then((d) => { if (d) setS(d); else setErr('Could not load settings.'); }); }, []);

  if (err && !s) return <div style={card}><ErrorText>{err}</ErrorText></div>;
  if (!s) return <div style={{ ...card, color: C.textMuted, fontSize: 13 }}>Loading…</div>;

  const set = <K extends keyof api.ReminderSettings>(k: K, v: api.ReminderSettings[K]) => setS({ ...s, [k]: v });

  async function save() {
    setBusy(true); setErr('');
    const r = await api.patchSettings({
      due_soon_lead_hours: s!.due_soon_lead_hours,
      overdue_cadence_hours: s!.overdue_cadence_hours,
      no_due_date_idle_days: s!.no_due_date_idle_days,
      remind_creator: s!.remind_creator,
      remind_unassigned: s!.remind_unassigned,
    });
    setBusy(false);
    if (!r.ok) { setErr(r.error || 'Could not save settings.'); return; }
    if (r.data) setS(r.data); // reflect the server's clamped values
    setToast('Task reminder settings saved.');
  }

  const numberRow = (key: 'due_soon_lead_hours' | 'overdue_cadence_hours' | 'no_due_date_idle_days', label: string, hint: string) => (
    <div style={{ marginBottom: 14 }}>
      <label style={labelStyle}>{label}</label>
      <div style={hintStyle}>{hint}</div>
      <input type="number" min={0} value={s![key]} onChange={(e) => set(key, nonNeg(e.target.value))} style={inputStyle} />
    </div>
  );
  const boolRow = (key: 'remind_creator' | 'remind_unassigned', label: string, hint: string) => (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, fontSize: 13, color: C.text }}>
        <input type="checkbox" checked={s![key]} onChange={(e) => set(key, e.target.checked)} style={{ accentColor: C.accent }} />
        {label}
      </label>
      <div style={{ ...hintStyle, marginLeft: 24, marginBottom: 0 }}>{hint}</div>
    </div>
  );

  return (
    <div style={card}>
      <h1 style={{ fontSize: 17, margin: '0 0 4px' }}>Task reminders</h1>
      <div style={{ fontSize: 12.5, color: C.textDim, marginBottom: 16 }}>
        How the system follows up open tasks. Changes apply on the next reminder sweep — no redeploy.
      </div>
      {numberRow('due_soon_lead_hours', 'Remind this many hours before a task is due', 'A single heads-up reminder this long before the due time.')}
      {numberRow('overdue_cadence_hours', 'Re-remind an overdue task every N hours', 'How often an overdue task is nudged again until it is actioned.')}
      {numberRow('no_due_date_idle_days', 'Nudge a no-due-date task after N days of no activity', '0 = never nudge dateless tasks. Otherwise a task with no due date is nudged after this many idle days, and again each idle period.')}
      {boolRow('remind_creator', "Also remind the task's creator", 'The person who raised the task is reminded too, not only the assignees.')}
      {boolRow('remind_unassigned', 'Remind the creator when a due task is unassigned', 'If nobody is assigned, nudge the creator so it is not forgotten.')}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 6 }}>
        <Btn kind="primary" disabled={busy} onClick={save}>Save settings</Btn>
        <ErrorText>{err}</ErrorText>
      </div>
    </div>
  );
}
